import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { Breadcrumb, Button, Card, Form } from 'react-bootstrap';
import Container from 'react-bootstrap/Container';
import { useForm, useWatch } from 'react-hook-form';
import { FaAngleLeft } from 'react-icons/fa6';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { ProviderQuestionnaireWrite, QuestionnaireService, UserTypeEnum } from '../api';
import { FormFeedback } from '../app/components/FormFeedback';
import { FormLabel } from '../app/components/FormLabel';
import { useCurrentUser } from '../app/hooks/auth';
import { useDocumentTitle } from '../app/hooks/use-document-title';
import { providerQueryKeys } from '../app/query-keys-factories/providers';
import { userQueryKeys } from '../app/query-keys-factories/user';
import { handleApiError } from '../app/utils/errorhandling';
import { ProviderQuestionAnswers } from './models';
import { getQuestionSchema } from './utils';

const validationSchema = z.object({
  licensureChallenges: getQuestionSchema(),
  liabilityIssues: getQuestionSchema(),
  liabilityActions: getQuestionSchema(),
  backgroundCheck: getQuestionSchema(),
  substanceUse: getQuestionSchema(),
});

export const ProviderQuestionnaire = () => {
  useDocumentTitle('Provider Questionnaire');
  const { providerId } = useParams<{ providerId: string }>();
  const { user } = useCurrentUser();
  const [searchParams] = useSearchParams();
  const nextUrl = searchParams.get('next');

  const isHealthEntity = useMemo(
    () => user?.user_type === UserTypeEnum.HEALTHCARE_ENTITY_REP,
    [user.user_type]
  );

  const navigate = useNavigate();

  const {
    control,
    register,
    reset,
    handleSubmit,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(validationSchema),
  });

  useQuery({
    queryKey: providerQueryKeys.questionnaire(providerId as string),
    queryFn: () => {
      return QuestionnaireService.questionnaireRetrieve({ providerId: providerId as string });
    },
    onSuccess({ data }) {
      const dataKeys = Object.keys(data);

      const defaultValues = dataKeys.reduce((acc, key) => {
        acc[key] = {
          value: data[key].value ? ProviderQuestionAnswers.Yes : ProviderQuestionAnswers.No,
          comment: data[key].comment,
        };

        return acc;
      }, {});

      reset(defaultValues);
    },
  });

  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async ({
      data,
      providerId,
    }: {
      data: ProviderQuestionnaireWrite;
      providerId: string;
    }) => {
      await QuestionnaireService.questionnaireUpdate({ requestBody: { data }, providerId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(providerQueryKeys.all());
      queryClient.invalidateQueries(userQueryKeys.detail('current'));
    },
  });

  const value = useWatch({ control });

  const handleSubmitClick = useCallback(() => {
    clearErrors();
  }, [clearErrors]);

  const onSubmit = useCallback(
    async (data: ProviderQuestionnaireWrite) => {
      try {
        if (!providerId) return;

        await mutation.mutateAsync({ data, providerId });
        navigate(nextUrl || './../../profile/other');
        toast.success('Provider Questionnaire updated successfully.');
      } catch (e) {
        handleApiError(e);
      }
    },
    [providerId, mutation, navigate, nextUrl]
  );

  return (
    <>
      <div className="bg-primary-dark">
        <Container className="pt-3 pb-1">
          <Link
            to={isHealthEntity ? './../other' : './../../profile/other'}
            className="text-decoration-none"
          >
            <Button
              variant="outline-light"
              size="sm"
              className="d-inline-flex align-items-center me-3"
            >
              <FaAngleLeft size={12} className="me-2" />
              Back
            </Button>
          </Link>

          <Breadcrumb className="breadcrumb-bg-primary-dark d-inline-block">
            <Breadcrumb.Item
              linkAs={Link}
              linkProps={{
                to: isHealthEntity ? './..' : './../..',
              }}
            >
              Profile
            </Breadcrumb.Item>
            <Breadcrumb.Item active>Provider Questionnaire</Breadcrumb.Item>
          </Breadcrumb>
        </Container>
      </div>
      <Container className="px-6" style={{ maxWidth: '800px' }}>
        <Card className="my-4">
          <Card.Header className="">
            <Card.Title className="fw-semibold fs-1 text-center text-primary mb-2 text-color-primary-dark">
              Provider Questionnaire
            </Card.Title>
          </Card.Header>
          <Card.Body className="p-4">
            <Form noValidate onSubmit={handleSubmit(onSubmit)}>
              <h3 className="mb-3">Licensure:</h3>
              <Form.Group>
                <FormLabel required={true}>
                  Have you faced any challenges to your licensure, registration, or certification,
                  or have you been investigated, sanctioned, reprimanded, or resigned under
                  investigation?
                </FormLabel>
                <div className="d-flex">
                  <Form.Check
                    type="radio"
                    className="d-flex flex-column align-items-center justify-content-center p-0 me-5 text-center"
                  >
                    <Form.Check.Input
                      type="radio"
                      disabled={isHealthEntity}
                      className="ms-0 mb-2"
                      value={ProviderQuestionAnswers.Yes}
                      id="licensureChallenges-yes"
                      {...register('licensureChallenges.value')}
                    />
                    <Form.Check.Label htmlFor="licensureChallenges-yes">Yes</Form.Check.Label>
                  </Form.Check>
                  <Form.Check
                    type="radio"
                    className="d-flex flex-column align-items-center justify-content-center p-0 text-center"
                  >
                    <Form.Check.Input
                      type="radio"
                      disabled={isHealthEntity}
                      className="ms-0 mb-2"
                      value={ProviderQuestionAnswers.No}
                      id="licensureChallenges-no"
                      {...register('licensureChallenges.value')}
                    />
                    <Form.Check.Label htmlFor="licensureChallenges-no">No</Form.Check.Label>
                  </Form.Check>
                </div>
                <FormFeedback clientError={errors?.['licensureChallenges']?.['value']} />
                <div className="mb-3" />
              </Form.Group>
              {value?.licensureChallenges?.value === 'Yes' && (
                <Form.Group className="mb-4">
                  <FormLabel required={true}>Please provide a comment:</FormLabel>
                  <Form.Control
                    className="min-h-120"
                    type="text"
                    disabled={isHealthEntity}
                    as="textarea"
                    isInvalid={!!errors?.['licensureChallenges']?.['comment']}
                    {...register('licensureChallenges.comment')}
                  />
                  <FormFeedback clientError={errors?.['licensureChallenges']?.['comment']} />
                </Form.Group>
              )}
              <h3 className="mb-3 pt-3">Professional Liability & Claims History:</h3>
              <Form.Group>
                <FormLabel required={true}>
                  Have you experienced any issues with your professional liability coverage, such as
                  cancellations, restrictions, surcharges, or high-risk ratings based on your
                  individual liability history?
                </FormLabel>
                <div className="d-flex">
                  <Form.Check
                    type="radio"
                    className="d-flex flex-column align-items-center justify-content-center p-0 me-5 text-center"
                  >
                    <Form.Check.Input
                      type="radio"
                      disabled={isHealthEntity}
                      className="ms-0 mb-2"
                      value={ProviderQuestionAnswers.Yes}
                      id="liabilityIssues-yes"
                      {...register('liabilityIssues.value')}
                    />
                    <Form.Check.Label htmlFor="liabilityIssues-yes">Yes</Form.Check.Label>
                  </Form.Check>
                  <Form.Check
                    type="radio"
                    className="d-flex flex-column align-items-center justify-content-center p-0 text-center"
                  >
                    <Form.Check.Input
                      type="radio"
                      disabled={isHealthEntity}
                      className="ms-0 mb-2"
                      value={ProviderQuestionAnswers.No}
                      id="liabilityIssues-no"
                      {...register('liabilityIssues.value')}
                    />
                    <Form.Check.Label htmlFor="liabilityIssues-no">No</Form.Check.Label>
                  </Form.Check>
                </div>
                <FormFeedback clientError={errors?.['liabilityIssues']?.['value']} />
                <div className="mb-3" />
              </Form.Group>
              {value?.liabilityIssues?.value === 'Yes' && (
                <Form.Group className="mb-4">
                  <FormLabel required={true}>Please provide a comment:</FormLabel>
                  <Form.Control
                    className="min-h-120"
                    type="text"
                    disabled={isHealthEntity}
                    as="textarea"
                    isInvalid={!!errors?.['liabilityIssues']?.['comment']}
                    {...register('liabilityIssues.comment')}
                  />
                  <FormFeedback clientError={errors?.['liabilityIssues']?.['comment']} />
                </Form.Group>
              )}
              <Form.Group>
                <FormLabel required={true}>
                  Have you ever had any professional liability actions (pending, settled,
                  arbitrated, mediated or litigated)?
                </FormLabel>
                <div className="d-flex">
                  <Form.Check
                    type="radio"
                    className="d-flex flex-column align-items-center justify-content-center p-0 me-5 text-center"
                  >
                    <Form.Check.Input
                      type="radio"
                      disabled={isHealthEntity}
                      className="ms-0 mb-2"
                      value={ProviderQuestionAnswers.Yes}
                      id="liabilityActions-yes"
                      {...register('liabilityActions.value')}
                    />
                    <Form.Check.Label htmlFor="liabilityActions-yes">Yes</Form.Check.Label>
                  </Form.Check>
                  <Form.Check
                    type="radio"
                    className="d-flex flex-column align-items-center justify-content-center p-0 text-center"
                  >
                    <Form.Check.Input
                      type="radio"
                      disabled={isHealthEntity}
                      className="ms-0 mb-2"
                      value={ProviderQuestionAnswers.No}
                      id="liabilityActions-no"
                      {...register('liabilityActions.value')}
                    />
                    <Form.Check.Label htmlFor="liabilityActions-no">No</Form.Check.Label>
                  </Form.Check>
                </div>
                <FormFeedback clientError={errors?.['liabilityActions']?.['value']} />
                <div className="mb-3" />
              </Form.Group>
              {value?.liabilityActions?.value === 'Yes' && (
                <Form.Group className="mb-4">
                  <FormLabel required={true}>Please provide a comment:</FormLabel>
                  <Form.Control
                    className="min-h-120"
                    type="text"
                    disabled={isHealthEntity}
                    isInvalid={!!errors?.['liabilityActions']?.['comment']}
                    as="textarea"
                    {...register('liabilityActions.comment')}
                  />
                  <FormFeedback clientError={errors?.['liabilityActions']?.['comment']} />
                </Form.Group>
              )}
              <h3 className="mb-3 pt-3">Background: </h3>
              <Form.Group>
                <FormLabel required={true}>
                  Is there anything you need to disclose that could be found on a background check
                  which could have a negative impact on your application(s) for hospital privileges?
                </FormLabel>
                <div className="d-flex">
                  <Form.Check
                    type="radio"
                    className="d-flex flex-column align-items-center justify-content-center p-0 me-5 text-center"
                  >
                    <Form.Check.Input
                      type="radio"
                      disabled={isHealthEntity}
                      className="ms-0 mb-2"
                      value={ProviderQuestionAnswers.Yes}
                      id="backgroundCheck-yes"
                      {...register('backgroundCheck.value')}
                    />
                    <Form.Check.Label htmlFor="backgroundCheck-yes">Yes</Form.Check.Label>
                  </Form.Check>
                  <Form.Check
                    type="radio"
                    className="d-flex flex-column align-items-center justify-content-center p-0 text-center"
                  >
                    <Form.Check.Input
                      type="radio"
                      disabled={isHealthEntity}
                      className="ms-0 mb-2"
                      value={ProviderQuestionAnswers.No}
                      id="backgroundCheck-no"
                      {...register('backgroundCheck.value')}
                    />
                    <Form.Check.Label htmlFor="backgroundCheck-no">No</Form.Check.Label>
                  </Form.Check>
                </div>
                <FormFeedback clientError={errors?.['backgroundCheck']?.['value']} />
                <div className="mb-3" />
              </Form.Group>
              {value?.backgroundCheck?.value === 'Yes' && (
                <Form.Group className="mb-4">
                  <FormLabel required={true}>Please provide a comment:</FormLabel>
                  <Form.Control
                    className="min-h-120"
                    type="text"
                    disabled={isHealthEntity}
                    as="textarea"
                    isInvalid={!!errors?.['backgroundCheck']?.['comment']}
                    {...register('backgroundCheck.comment')}
                  />
                  <FormFeedback clientError={errors?.['backgroundCheck']?.['comment']} />
                </Form.Group>
              )}
              <h3 className="mb-3 pt-3">Job Performance:</h3>
              <Form.Group>
                <FormLabel required={true}>
                  Do you currently use any substances, legal or illegal, that could impair your
                  ability to perform your job safely and effectively, or do you have any reason to
                  believe you would pose a risk to patient safety or well-being?
                </FormLabel>
                <div className="d-flex">
                  <Form.Check
                    type="radio"
                    className="d-flex flex-column align-items-center justify-content-center p-0 me-5 text-center"
                  >
                    <Form.Check.Input
                      type="radio"
                      disabled={isHealthEntity}
                      className="ms-0 mb-2"
                      value={ProviderQuestionAnswers.Yes}
                      id="substanceUse-yes"
                      {...register('substanceUse.value')}
                    />
                    <Form.Check.Label htmlFor="substanceUse-yes">Yes</Form.Check.Label>
                  </Form.Check>
                  <Form.Check
                    type="radio"
                    className="d-flex flex-column align-items-center justify-content-center p-0 text-center"
                  >
                    <Form.Check.Input
                      type="radio"
                      disabled={isHealthEntity}
                      className="ms-0 mb-2"
                      value={ProviderQuestionAnswers.No}
                      id="substanceUse-no"
                      {...register('substanceUse.value')}
                    />
                    <Form.Check.Label htmlFor="substanceUse-no">No</Form.Check.Label>
                  </Form.Check>
                </div>
                <FormFeedback clientError={errors?.['substanceUse']?.['value']} />
                <div className="mb-3" />
              </Form.Group>
              {value?.substanceUse?.value === 'Yes' && (
                <Form.Group className="mb-4">
                  <FormLabel required={true}>Please provide a comment:</FormLabel>
                  <Form.Control
                    className="min-h-120"
                    type="text"
                    disabled={isHealthEntity}
                    as="textarea"
                    isInvalid={!!errors?.['substanceUse']?.['comment']}
                    {...register('substanceUse.comment')}
                  />
                  <FormFeedback clientError={errors?.['substanceUse']?.['comment']} />
                </Form.Group>
              )}
              {!isHealthEntity && (
                <div className="d-grid py-3 mt-5">
                  <Button
                    className="fs-4 rounded shadow"
                    type="submit"
                    variant="primary"
                    disabled={mutation.isLoading}
                    onAbort={handleSubmitClick}
                  >
                    Submit
                  </Button>
                </div>
              )}
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};
